import i18n from '@/i18n';
import { aiText2ImgPromptCaseAPI } from '@/utils/api';
import {
  AI_TEXT2IMG_STYLE_OPTIONS,
} from '@/pages/ipe/workbench/constants/aiStudioConfig';
import {
  DEFAULT_TITLE_PRESET_SCOPE,
  normalizeTitlePresetScope,
  resolveVisibleTitlePresetScopes,
} from '@/pages/ipe/workbench/constants/titlePresetCatalog';

export const TEXT2IMG_CASE_DEFAULT_PAGE_SIZE = 20;
export const TEXT2IMG_CASE_PAGE_SIZE_OPTIONS = [20, 50, 100, 200];

export function getText2ImgStyleLabel(style) {
  const code = String(style || '').trim();
  const found = AI_TEXT2IMG_STYLE_OPTIONS.find((o) => o.value === code);
  return found?.label || code || '—';
}

/** 将后端 CaseView 转为模板库统一结构（fallbackScope：后端未返回 caseScope 时的兜底） */
export function mapText2ImgPromptCaseApiItem(item, fallbackScope = 'official') {
  if (!item || typeof item !== 'object') return null;
  const description = String(item.description || '').trim();
  const name = String(item.name || '').trim() || '未命名模板';
  const id = item.id != null ? String(item.id) : '';
  if (!id) return null;
  const status = item.status != null ? Number(item.status) : 1;
  if (status === 0) return null;
  const style = String(item.style || 'common').trim() || 'common';
  return {
    id,
    name,
    description: description || name,
    style,
    styleLabel: item.styleLabel || getText2ImgStyleLabel(style),
    aspectRatio: String(item.aspectRatio || '1:1').trim() || '1:1',
    scope: normalizeTitlePresetScope(item.caseScope || item.scope || fallbackScope),
    canEdit: Boolean(item.canEdit),
    canDelete: Boolean(item.canDelete),
    ownerUserId: item.ownerUserId ?? null,
    ownerDisplayName: String(item.ownerDisplayName || '').trim(),
    sortWeight: Number(item.sortWeight) || 0,
    source: 'api',
  };
}

export function filterText2ImgCasesByScope(cases, scope) {
  const key = normalizeTitlePresetScope(scope || DEFAULT_TITLE_PRESET_SCOPE);
  return (Array.isArray(cases) ? cases : []).filter((item) => item.scope === key);
}

export function filterText2ImgCasesByKeyword(cases, keyword) {
  const q = String(keyword || '').trim().toLowerCase();
  if (!q) return Array.isArray(cases) ? cases : [];
  return (Array.isArray(cases) ? cases : []).filter((item) => {
    const hay = `${item.name || ''} ${item.description || ''} ${item.styleLabel || ''} ${item.aspectRatio || ''}`.toLowerCase();
    return hay.includes(q);
  });
}

/**
 * 按 scope 真分页拉取文生图模板（个人 / 团队 / 官方）。
 * includeOfficial=true 时后端会把「官方」合并进本次查询（前端模板库锁定作用域时使用）。
 */
export async function loadText2ImgPromptCasesPage({
  scope = DEFAULT_TITLE_PRESET_SCOPE,
  keyword = '',
  ownerUserId = '',
  page = 1,
  pageSize = TEXT2IMG_CASE_DEFAULT_PAGE_SIZE,
  includeOfficial = false,
} = {}) {
  const safeScope = normalizeTitlePresetScope(scope);
  const ownerId = ownerUserId != null && String(ownerUserId).trim() !== ''
    ? String(ownerUserId).trim()
    : undefined;
  const res = await aiText2ImgPromptCaseAPI.list({
    scope: safeScope,
    keyword: String(keyword || '').trim() || undefined,
    ownerUserId: safeScope === 'team' ? ownerId : undefined,
    page,
    pageSize,
    includeOfficial: includeOfficial ? true : undefined,
  });
  const items = (Array.isArray(res?.items) ? res.items : [])
    .map((raw) => mapText2ImgPromptCaseApiItem(raw, safeScope))
    .filter(Boolean);
  return {
    items,
    total: Number(res?.total) || 0,
    page: Number(res?.page) || page || 1,
    pageSize: Number(res?.pageSize) || pageSize || TEXT2IMG_CASE_DEFAULT_PAGE_SIZE,
    caseScope: res?.caseScope || safeScope,
  };
}

/** @deprecated 全量拉取已废弃，保留兼容旧调用 */
export async function loadText2ImgPromptCasesForStudio() {
  const scopes = ['personal', 'team', 'official'];
  const settled = await Promise.all(
    scopes.map(async (scope) => {
      try {
        const res = await loadText2ImgPromptCasesPage({
          scope,
          page: 1,
          pageSize: 200,
        });
        return { scope, items: res.items, ok: true };
      } catch (_) {
        return { scope, items: [], ok: false };
      }
    }),
  );
  return settled.flatMap((row) => row.items || []);
}

export function resolveText2ImgCaseOwnerLabel(item, { currentUserId } = {}) {
  if (!item) return '';
  if (item.scope === 'official') return i18n.t('workbench:studioChrome.official');
  const ownerId = item.ownerUserId != null ? String(item.ownerUserId) : '';
  const me = currentUserId != null ? String(currentUserId) : '';
  if (ownerId && me && ownerId === me) return i18n.t('workbench:ipeUtilError.scopeMe');
  if (item.ownerDisplayName) return item.ownerDisplayName;
  return ownerId ? `用户 ${ownerId}` : '—';
}

/** 团队 Tab 归属者下拉：全部 + 花名册成员 */
export function buildText2ImgCaseOwnerFilterOptions({
  ownerNameByUserId = null,
  currentUserId = '',
} = {}) {
  const all = { value: '', label: '全部归属人' };
  const map = ownerNameByUserId && typeof ownerNameByUserId === 'object'
    ? ownerNameByUserId
    : {};
  const me = currentUserId != null ? String(currentUserId) : '';
  const seen = new Set();
  const options = [];

  const push = (id, name) => {
    const key = String(id || '').trim();
    if (!key || seen.has(key)) return;
    seen.add(key);
    const display = String(name || '').trim();
    if (me && key === me) {
      options.push({ value: key, label: display ? `${display}（我）` : '我' });
      return;
    }
    options.push({ value: key, label: display || `用户 ${key}` });
  };

  if (me) push(me, map[me] || map[Number(me)] || '');
  Object.keys(map)
    .sort((a, b) => String(map[a] || a).localeCompare(String(map[b] || b), 'zh-CN'))
    .forEach((id) => push(id, map[id]));

  return [all, ...options];
}

export { resolveVisibleTitlePresetScopes, DEFAULT_TITLE_PRESET_SCOPE };

/** 工作流执行：解析 prompt / 风格 / 比例（可选用案例 ID 补全） */
export async function resolveText2ImgWorkflowParams(params = {}) {
  let prompt = String(params.prompt || '').trim();
  let style = String(params.style || 'common').trim() || 'common';
  let aspectRatio = String(params.aspectRatio || '1:1').trim() || '1:1';
  const caseId = String(params.text2imgPromptCaseId || '').trim();
  if (!prompt && caseId) {
    try {
      const res = await aiText2ImgPromptCaseAPI.getById(caseId);
      const mapped = mapText2ImgPromptCaseApiItem(res?.case);
      if (mapped) {
        prompt = String(mapped.description || '').trim();
        if (mapped.style) style = String(mapped.style);
        if (mapped.aspectRatio) aspectRatio = String(mapped.aspectRatio);
      }
    } catch {
      // ignore
    }
  }
  if (!AI_TEXT2IMG_STYLE_OPTIONS.some((o) => o.value === style)) style = 'common';
  if (!['1:1', '2:3', '3:2', '3:4', '4:3', '9:16', '16:9'].includes(aspectRatio)) {
    aspectRatio = '1:1';
  }
  return { prompt, style, aspectRatio };
}
